import { Injectable } from "@angular/core";
import * as AWS from "aws-sdk";
import { JwtService } from "../../core/services";
import { environment } from "../../../environments/environment";
import { SessionStateService } from "./../../core/services/session-state.service";
import { DevicesService } from "./../../entities/devices/devices.service";
import { StateVariables } from "../../entities/devices/state-variables.model";
import { Observable } from "rxjs/Observable";
import { ApiService } from "./api.service";
import { RealTimeMessage } from "../../entities/devices/real-time-message.model";
import { Subject } from "rxjs/Subject";
import { Device } from "../../entities/devices/devices.model_new";
import { STATE_VAR } from "../../entities/devices/devices.constant";
import { Router } from "@angular/router";
import "rxjs/add/operator/catch";
import "rxjs/add/observable/throw";

const uuidv4 = require("uuid/v4");
@Injectable()
export class IotService {
  awsIot = require("aws-iot-device-sdk");
  public device;

  private identityPool: string = environment.aws.iot.IdentityPoolId;
  private region: string = environment.aws.iot.Region;
  private login: string = environment.aws.iot.Login;

  private protocol: string = environment.aws.iot.Connection.protocol;
  private host: string = environment.aws.iot.Connection.host;
  private port: number = environment.aws.iot.Connection.port;

  stateVariables: StateVariables;

  messagesObserver: Subject<any>;

  requestToDevicesMap: Map<string, any>;
  updatingDevicesSet: Set<string>;

  responseHistory: Map<string, any>;

  private keepAliveInterval: NodeJS.Timer;
  private retryInterval: NodeJS.Timer;
  private isIoTConnectionEstablished: boolean = false;
  private retryNumber: number = 1;

  // Constant
  private IOT_INTERVAL_CONNECTION_MS: number = 57000;
  private IOT_CONNECTION_RETRY_MS: number = 2000;
  private MAX_RETRIES_NUMBER: number = 5;

  constructor(
    private apiService: ApiService,
    private jwtService: JwtService,
    private sessionStateService: SessionStateService,
    private devicesService: DevicesService,
    private router: Router
  ) {
    this.responseHistory = new Map<string, any>();
  }

  private startConnection() {
    this.establishConnection().subscribe();
  }

  keepConnectionAlive(smartDevice) {
    const { deviceLocalId, type } = smartDevice;

    const smartDevicesItems = [
      { type: "redybox", initials: "rb" },
      { type: "wifi", initials: "wifi" },
    ];

    this.keepAliveInterval = setInterval(() => {
      const deviceLocalIdTemp = deviceLocalId;

      const realtime = {
        id: `Portal_${uuidv4()}`,
        operationType: "realtime",
        messageType: "request",
        data: {
          timeout: 60,
        },
      };

      for (const item of smartDevicesItems) {
        if (item.type === type) {
          this.device.publish(
            `${item.initials}/${deviceLocalIdTemp}/toDev/realtime`,
            JSON.stringify(realtime)
          );
        }
      }
    }, this.IOT_INTERVAL_CONNECTION_MS);
  }

  private configureConnection() {
    if (this.device) {
      this.device.end();
    }

    this.device = this.awsIot.device({
      accessKeyId: AWS.config.credentials.accessKeyId,
      secretKey: AWS.config.credentials.secretAccessKey,
      sessionToken: AWS.config.credentials.sessionToken,
      protocol: this.protocol,
      host: this.host,
      port: this.port,
      clientId: `Portal-${uuidv4()}`,
    });
  }

  public establishConnection(): Observable<any> {
    return this.devicesService
      .getHouseDevice(this.sessionStateService.getHouse().houseId)
      .map((response) => {
        // Create MQTT connection with IoT
        this.configureConnection();
        const smartDevices = [];

        response.forEach((smartDevice) => {
          const smartDevicesItems = [
            { type: "redybox", initials: "rb" },
            { type: "wifi", initials: "wifi" },
          ];

          for (const item of smartDevicesItems) {
            if (item.type === smartDevice.type) {
              smartDevices.push(smartDevice);

              const smartDeviceId = smartDevice.deviceLocalId;

              this.device.subscribe(
                `${item.initials}/${smartDeviceId}/fromDev/module/update`
              );
              this.device.subscribe(
                `${item.initials}/${smartDeviceId}/fromDev/module/changed`
              );
              this.device.subscribe(
                `${item.initials}/${smartDeviceId}/fromDev/realtime`
              );

              // Publish Realtime Mode
              const realtime = {
                id: `Portal_${uuidv4()}`,
                operationType: "realtime",
                messageType: "request",
                data: {
                  timeout: 60,
                },
              };

              this.device.publish(
                `${item.initials}/${smartDeviceId}/toDev/realtime`,
                JSON.stringify(realtime)
              );
              this.obtainRealTimeResponses();
              this.keepConnectionAlive(smartDevice);
            }
          }
        });

        this.sessionStateService.setSmartDevices(smartDevices);

        this.isIoTConnectionEstablished = true;
        return Observable.empty();
      })
      .catch(() => {
        return Observable.empty();
      });
  }

  connect() {
    if (this.isIoTConnectionEstablished) {
      return;
    }

    AWS.config.region = this.region;
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: this.identityPool,
      Logins: {
        [this.login]: this.jwtService.getIdToken(),
      },
    });
    this.messagesObserver = new Subject<any>();
    this.requestToDevicesMap = new Map<string, any>();
    this.updatingDevicesSet = new Set<string>();
    AWS.config.getCredentials((err) => {
      if (!err) {
        if (this.device !== undefined) {
          this.disconnect();
        }
        if (this.stateVariables === undefined) {
          this.getStateVariables().subscribe((data) => {
            this.stateVariables = data;
            this.startConnection();
          });
        } else {
          this.startConnection();
        }
      } else {
        this.disconnect();
      }
    });
  }

  disconnect() {
    this.isIoTConnectionEstablished = false;
    if (this.device !== undefined) {
      this.device.end();
      this.device = undefined;
    }
    if (this.messagesObserver !== undefined) {
      this.messagesObserver.unsubscribe();
      this.messagesObserver = undefined;
    }
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
    }
  }

  private obtainRealTimeResponses() {
    this.device.on("message", (topic, payload) => {
      try {
        const realTimeMessage = JSON.parse(payload);
        const { messageType, operationType, data } = realTimeMessage;

        if (
          (messageType === "notification" || messageType === "request") &&
          operationType === "moduleChanged"
        ) {
          for (const dataRealTime of data) {
            const message = RealTimeMessage.fromDTO(
              messageType,
              operationType,
              dataRealTime
            );
            const moduleLocalId = message.data.localId;

            for (const stateVariable in message.data.stateVariables) {
              const hardwareKey = stateVariable;
              const platformKey =
                this.stateVariables.hardwareToPlatform(hardwareKey);
              const value = message.data.stateVariables[hardwareKey];
              this.updatingDevicesSet.delete(`${moduleLocalId}#${hardwareKey}`);
              const response = {
                moduleLocalId: moduleLocalId,
                platformKey: platformKey,
                value: value,
              };
              this.responseHistory.set(moduleLocalId, response);
              if (this.messagesObserver) {
                this.messagesObserver.next(response);
              }
            }
          }
        } else if (realTimeMessage.messageType === "response") {
          this.receiveResponseMessage(
            realTimeMessage.id,
            realTimeMessage.success
          );
        }
      } catch (error) {
        console.log(error);
      }
    });
  }

  updateModule(module: Device, stateVar: STATE_VAR, newValue: any): void {
    const smartDevice = this.sessionStateService
      .getSmartDevices()
      .find((smartDevice) => smartDevice.deviceId === module.getDeviceId());

    if (smartDevice) {
      const smartDeviceId = smartDevice.deviceLocalId;
      const moduleLocalId = module.getModuleLocalId();
      const requestStateVar = module.getStateVar(stateVar).action.name;
      const hardwareKey = this.stateVariables.platformToHardware(stateVar);
      if (stateVar === "setpoint") {
        newValue = Number(newValue);
      }

      if (stateVar === STATE_VAR.RELAY_STATE) {
        newValue = newValue ? "ON" : "OFF";
      }
      const requestObject: any = {};
      requestObject[requestStateVar] = newValue;
      const messageUpdateModule = {
        id: `Portal_${uuidv4()}`,
        operationType: "updateModule",
        messageType: "request",
        data: {
          localId: moduleLocalId,
          stateVariables: requestObject,
        },
      };

      this.requestToDevicesMap.set(messageUpdateModule.id, {
        moduleLocalId: moduleLocalId,
        stateVar: hardwareKey,
      });
      this.updatingDevicesSet.add(`${moduleLocalId}#${hardwareKey}`);

      const smartDevicesItems = [
        { type: "redybox", initials: "rb" },
        { type: "wifi", initials: "wifi" },
      ];

      for (const item of smartDevicesItems) {
        if (item.type === smartDevice.type) {
          this.device.publish(
            `${item.initials}/${smartDeviceId}/toDev/module/update`,
            JSON.stringify(messageUpdateModule)
          );
        }
      }
    }
  }

  private getStateVariables(): Observable<StateVariables> {
    return this.apiService
      .get("/interfacedata/modules/statevariables")
      .map((res) => StateVariables.fromDTOWrapper(res))
      .catch((error) => {
        return Observable.throw(error);
      });
  }

  receiveResponseMessage(messageId: string, success: boolean): void {
    const requestMessage = this.requestToDevicesMap.get(messageId);
    if (requestMessage) {
      this.updatingDevicesSet.delete(
        `${requestMessage.moduleLocalId}#${requestMessage.stateVar}`
      );
    }
  }

  isUpdatingModule(moduleLocaleId): boolean {
    return Array.from(this.updatingDevicesSet).some(
      (d) => d.split("#")[0] === moduleLocaleId
    );
  }

  getDevice() {
    return this.device;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/iot.service.ts